/**
 * Keyboard machinery for the `.context__menu` lists rendered inside
 * `Context.vue`.
 *
 * `role="menu"` is a promise, not a label: the W3C WAI-ARIA APG menu pattern
 * says that a node carrying it must let the user move between its items with
 * Up/Down, jump to the ends with Home/End, and leave with Escape. Applying the
 * role without that machinery is worse than applying no role at all, because
 * it also strips the list semantics a screen reader otherwise derives from the
 * `<ul>`/`<li>` markup. This module is the machinery, so the role can be
 * applied honestly.
 *
 * Everything here reads the DOM at keypress time rather than caching an index
 * at mount. Menus are built out of `v-if`-ed permission checks and
 * `<component :is>` slots, so the set of items a user can actually reach
 * changes between one keypress and the next.
 */

// A menu item is the focusable control inside `<li class="context__menu-item">`.
const MENU_ITEM_SELECTOR = '.context__menu-item-link'

// The list element that a menu item lives in.
const MENU_SELECTOR = 'ul.context__menu'

/**
 * Whether the element is an item the user can actually move to.
 *
 * Disabled is spelled three ways across the call sites -- the native
 * `disabled` attribute, a `.disabled` class (styled in `context.scss`), and
 * `aria-disabled` -- so all three are honoured. `--loading` items stay
 * reachable on purpose: they are mid-action, not unavailable, and yanking
 * focus off the item a user just activated is disorienting.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isReachableItem = (element) => {
  if (element.disabled === true) {
    return false
  }
  if (element.classList.contains('disabled')) {
    return false
  }
  if (element.getAttribute('aria-disabled') === 'true') {
    return false
  }
  // `offsetParent` is null for anything `display: none`, which covers the
  // `v-show`-ed menus and hidden sub-lists. jsdom does not lay out, so it
  // reports null for everything; the `hidden` attribute and inline
  // `display: none` are checked directly so the intent survives there too.
  if (element.hidden || element.style.display === 'none') {
    return false
  }
  const li = element.closest('.context__menu-item')
  if (li && (li.hidden || li.style.display === 'none')) {
    return false
  }
  return true
}

/**
 * All items of `menuEl` the user can move to, in DOM order.
 *
 * @param {HTMLElement} menuEl
 * @returns {HTMLElement[]}
 */
export const getMenuItems = (menuEl) => {
  if (!menuEl) {
    return []
  }
  return Array.from(menuEl.querySelectorAll(MENU_ITEM_SELECTOR)).filter(
    isReachableItem
  )
}

/**
 * The menu lists inside `rootEl` that are real menus.
 *
 * A `.context__menu` that contains no `.context__menu-item-link` is a styled
 * list, not a menu -- `ExpandOnOverflowHiddenContext` uses one to show
 * overflowed record names. Those get no role and no key handling, which is why
 * the controller detects structure instead of trusting the class name.
 *
 * @param {HTMLElement} rootEl
 * @returns {HTMLElement[]}
 */
export const findMenus = (rootEl) => {
  if (!rootEl) {
    return []
  }
  return Array.from(rootEl.querySelectorAll(MENU_SELECTOR)).filter(
    (menuEl) => menuEl.querySelector(MENU_ITEM_SELECTOR) !== null
  )
}

/**
 * Whether arrow keys belong to the element the user is currently on rather
 * than to the menu.
 *
 * Several contexts put a search field or a nested editor above their list. In
 * a text field the arrows move the caret, and in a `<select>` they change the
 * value; stealing them there would break the control the user is actually
 * typing into. `contenteditable` is included for the rich-text cells.
 *
 * @param {Element|null} element
 * @returns {boolean}
 */
export const consumesArrowKeys = (element) => {
  if (!element || element.nodeType !== 1) {
    return false
  }
  const tag = element.tagName
  if (tag === 'INPUT') {
    // Checkboxes and radios do not use the arrows for a caret, but they are
    // not menu items either, so leaving them alone is still the safe call.
    return true
  }
  if (tag === 'TEXTAREA' || tag === 'SELECT') {
    return true
  }
  return element.isContentEditable === true
}

/**
 * Applies the roving tabindex: exactly one item of the menu is in the tab
 * order, the rest are reachable only with the arrows.
 *
 * The APG requires this. Without it a menu of fifteen commands puts fifteen
 * stops in the page's tab sequence, and Tab -- which should move past the menu
 * -- walks through it instead.
 *
 * @param {HTMLElement[]} items
 * @param {HTMLElement|null} activeItem  Item to make tabbable. Defaults to the
 *                                       first item when absent or not in the list.
 */
export const applyRovingTabindex = (items, activeItem = null) => {
  if (items.length === 0) {
    return
  }
  const active = items.includes(activeItem) ? activeItem : items[0]
  for (const item of items) {
    item.setAttribute('tabindex', item === active ? '0' : '-1')
  }
}

/**
 * Moves focus to `item` and rolls the tab stop over to it.
 *
 * @param {HTMLElement[]} items
 * @param {HTMLElement} item
 */
const focusItem = (items, item) => {
  applyRovingTabindex(items, item)
  item.focus()
}

/**
 * Index the arrow keys should move to, wrapping at both ends.
 *
 * @param {number} currentIndex  -1 when focus is not on an item yet.
 * @param {number} length
 * @param {number} step          1 for Down, -1 for Up.
 * @returns {number}
 */
const nextIndex = (currentIndex, length, step) => {
  if (currentIndex === -1) {
    // Entering the menu from outside: Down lands on the first item, Up on the
    // last, which is what the APG asks of a menu button.
    return step === 1 ? 0 : length - 1
  }
  return (currentIndex + step + length) % length
}

/**
 * Handles one keydown for the menus inside `rootEl`.
 *
 * Returns true when the key was consumed, so the caller can tell whether the
 * event still belongs to anything else.
 *
 * | Key        | Behaviour                                    |
 * | ---------- | -------------------------------------------- |
 * | ArrowDown  | next item, wrapping to the first             |
 * | ArrowUp    | previous item, wrapping to the last          |
 * | Home       | first item                                   |
 * | End        | last item                                    |
 *
 * Escape is deliberately absent: `Context.vue` already owns it, and it has to,
 * because only the context knows which layer of a nested stack is innermost.
 * Type-ahead is also absent -- see the note in `Context.vue`.
 *
 * @param {KeyboardEvent} event
 * @param {HTMLElement} rootEl  Teleported root of the context.
 * @returns {boolean}
 */
export const handleMenuKeyDown = (event, rootEl) => {
  const key = event.key
  if (
    key !== 'ArrowDown' &&
    key !== 'ArrowUp' &&
    key !== 'Home' &&
    key !== 'End'
  ) {
    return false
  }
  // A modified arrow is a browser or OS gesture (Alt+Left is Back), not menu
  // navigation.
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
    return false
  }
  if (!rootEl) {
    return false
  }

  const active = document.activeElement

  // The field the user is typing in wins over the menu.
  if (consumesArrowKeys(active)) {
    return false
  }

  const menus = findMenus(rootEl)
  if (menus.length === 0) {
    return false
  }

  // With more than one menu in a context, the arrows drive the one focus is
  // already inside; otherwise the first. Multiple menus in a single context
  // are rare, but picking arbitrarily would strand focus.
  const focusedMenu = menus.find((menuEl) => menuEl.contains(active))
  const menuEl = focusedMenu || menus[0]

  const items = getMenuItems(menuEl)
  if (items.length === 0) {
    return false
  }

  // Focus sitting on something else inside the context -- a header button, a
  // toggle above the list -- is left alone. Only a menu item or the context
  // itself hands the arrows to the menu.
  if (
    active &&
    active !== document.body &&
    rootEl.contains(active) &&
    active !== rootEl &&
    !items.includes(active)
  ) {
    return false
  }

  const currentIndex = items.indexOf(active)

  let target
  if (key === 'Home') {
    target = items[0]
  } else if (key === 'End') {
    target = items[items.length - 1]
  } else {
    target =
      items[nextIndex(currentIndex, items.length, key === 'ArrowDown' ? 1 : -1)]
  }

  event.preventDefault()
  event.stopPropagation()
  focusItem(items, target)
  return true
}

/**
 * Marks the menus inside `rootEl` up for assistive technology and seeds the
 * roving tabindex.
 *
 * Only lists that passed `findMenus` are touched, so the role is never a
 * promise the key handling cannot keep. `role="none"` on the `<li>` is what the
 * APG requires: a `menuitem` must be a direct child of the `menu` in the
 * accessibility tree, so the intervening list item has to be stripped of its
 * implicit `listitem` role.
 *
 * @param {HTMLElement} rootEl
 */
export const applyMenuRoles = (rootEl) => {
  for (const menuEl of findMenus(rootEl)) {
    menuEl.setAttribute('role', 'menu')

    const allItems = Array.from(menuEl.querySelectorAll(MENU_ITEM_SELECTOR))
    for (const item of allItems) {
      item.setAttribute('role', 'menuitem')
      const li = item.closest('.context__menu-item')
      if (li && menuEl.contains(li)) {
        li.setAttribute('role', 'none')
      }
      // Disabled items keep their role but are announced as unavailable, which
      // is why `getMenuItems` can skip them without them vanishing from the
      // count a screen reader reads out.
      if (!isReachableItem(item)) {
        item.setAttribute('aria-disabled', 'true')
      }
    }

    const items = allItems.filter(isReachableItem)
    const focused = items.find((item) => item === document.activeElement)
    applyRovingTabindex(items, focused || null)
  }
}
